import React from 'react';

import { Box, Flex, SimpleGrid, useTheme } from '@chakra-ui/react';

// Helpers
import { hexToRGB } from 'constants/helper';

import { TextBodyMedium, TextLabelSmall } from 'uiToolkitV1/Typography/Typography';

const GRID_COLOR_MAPPING = [
  // {
  //   '#FFEFD4': '#8C6A00',
  // },
  {
    '#DBF2D3': '#44812F',
  },
  // {
  //   '#BFE8C6': '#1A5724',
  // },
  {
    '#FFE7C5': '#AF5C20',
  },
  {
    '#FAD4D4': '#C62C02',
  },
  {
    '#EAEAEA': '#333333',
  },
  {
    '#E6F1FD': '#1565BC',
  },
];

const getColWidths = ({ rows, columns, boxData = [] }) => {
  let widthCSSString = '';
  if (!boxData || boxData?.length === 0) return widthCSSString;

  const bottomMostRowData = boxData.slice(0, columns);

  widthCSSString = bottomMostRowData.reduce((cssString, singleBoxData) => {
    const dataKey = rows === 1 ? 'OVERALL' : 'PERFORMANCE';
    const max = singleBoxData?.scoreRange?.[dataKey]?.maxScore ?? 0;
    const min = singleBoxData?.scoreRange?.[dataKey]?.minScore ?? 0;
    return `${cssString} minmax(20px, ${max - min}fr)`;
  }, '');

  return widthCSSString;
};

function PerformanceGridLayout({
  rows = 3,
  columns = 3,
  spacing = '5px',
  boxHeight = '72',
  boxData = [],
  position = '',
  margin = '0',
  width = '100%',
  left = 0,
  top = 0,
  isExpanded,
  isTextCenterAlign,
  sx = {},
}) {
  const { colors } = useTheme();
  const TextComp = isExpanded ? TextBodyMedium : TextLabelSmall;

  // Find the color mapping for a given key
  function getColorMapping(key) {
    const mapping = GRID_COLOR_MAPPING.find((entry) => entry[key]);
    return mapping ? mapping[key] : key;
  }

  return (
    <Box
      position={position}
      top={top}
      left={left}
      width={width}
      zIndex={0}
      margin={margin}
      transform="scaleY(-1)"
      sx={sx}
    >
      <SimpleGrid
        templateRows={`repeat(${rows}, 1fr)`}
        templateColumns={getColWidths({ rows, columns, boxData })}
        spacing={spacing}
      >
        {boxData?.map((box, index) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={box.label + box.color + index}
            bg={box?.color ? hexToRGB(box.color, 1) : colors.neutral.CFD7DF}
            height={boxHeight}
            padding="8px"
            transform="scaleY(-1)"
          >
            {isTextCenterAlign ? (
              <Flex
                style={{
                  hyphens: 'auto',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <TextComp color={getColorMapping(box.color)} filter="brightness(0.75)">
                  {box?.label}
                </TextComp>
                <Flex>
                  {box?.count !== undefined && (
                    <TextComp mr="2px" color={getColorMapping(box.color)} filter="brightness(0.75)">
                      {box?.count}
                    </TextComp>
                  )}
                  {box?.percentage !== undefined && (
                    <TextComp filter="brightness(0.75)" color={getColorMapping(box.color)}>
                      ({box?.percentage}%)
                    </TextComp>
                  )}
                </Flex>
              </Flex>
            ) : (
              <Flex style={{ hyphens: 'auto' }}>
                <TextComp color={getColorMapping(box.color)} filter="brightness(0.75)">
                  {box?.label}
                </TextComp>
                <Flex ml="auto">
                  {box?.count !== undefined && (
                    <TextComp color={getColorMapping(box.color)} filter="brightness(0.75)">
                      {box?.count}
                    </TextComp>
                  )}
                  {box?.percentage !== undefined && (
                    <TextComp filter="brightness(0.75)" color={getColorMapping(box.color)}>
                      ({box?.percentage}%)
                    </TextComp>
                  )}
                </Flex>
              </Flex>
            )}
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default PerformanceGridLayout;
